import { useState } from 'react';
import { Copy } from 'react-bootstrap-icons';

import type { SDSIconColor, SDSIconSize } from '@repo/sds/SDSIcon';
import { SDSIconButton } from '@repo/sds/SDSIconButton';
import { SDSTooltip } from '@repo/sds/SDSTooltip';
import { toast } from '@repo/sds/utils/toast';

import './ClipboardCopyIconTooltip.scss';

export interface ClipboardCopyIconTooltipProps {
  /** String written to the clipboard on success. */
  textToCopy: string;
  /** Tooltip label before copy (e.g. hover). */
  idleTooltip: string;
  /** Tooltip label immediately after a successful copy. */
  copiedTooltip: string;
  ariaLabel: string;
  dataTestId?: string;
  /** `data-testid` on the tooltip surface (popup), not the icon button. */
  tooltipDataTestId?: string;
  isDisabled?: boolean;
  size?: SDSIconSize;
  color?: SDSIconColor;
  /** Shown via `toast.error` when `navigator.clipboard.writeText` rejects. */
  copyErrorToastMessage?: string;
}

/**
 * Icon button that copies text to the clipboard and shows idle vs. success labels in a tooltip.
 * Remount with a different `key` when surrounding context changes (e.g. tab) so feedback resets.
 */
export function ClipboardCopyIconTooltip({
  textToCopy,
  idleTooltip,
  copiedTooltip,
  ariaLabel,
  dataTestId,
  tooltipDataTestId,
  isDisabled,
  size,
  color,
  copyErrorToastMessage = 'Failed to copy',
}: ClipboardCopyIconTooltipProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [copyAcknowledged, setCopyAcknowledged] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setTooltipOpen(open);
    if (!open) {
      setCopyAcknowledged(false);
    }
  };

  const handleClick = async () => {
    if (isDisabled) {
      return;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyAcknowledged(true);
      setTooltipOpen(true);
    } catch {
      toast.error(copyErrorToastMessage);
    }
  };

  return (
    <SDSTooltip
      open={tooltipOpen}
      onOpenChange={handleOpenChange}
      content={copyAcknowledged ? copiedTooltip : idleTooltip}
      dataTestId={tooltipDataTestId}
      side="bottom"
      align="center"
      showArrow={false}
      delay={400}
      closeDelay={100}
      className="ClipboardCopyIconTooltip__trigger"
      renderTrigger={<span />}
    >
      <SDSIconButton
        size={size ?? 'medium'}
        color={color}
        icon={<Copy />}
        onClick={handleClick}
        isDisabled={isDisabled}
        ariaLabel={ariaLabel}
        dataTestId={dataTestId}
      />
    </SDSTooltip>
  );
}
